import { getCustomerList, getCustomerDetails } from '../APICalling/APICalling';
import {
	buildCustomerListRequest,
	buildMembershipCustomerListExtraParams,
} from './customerSearchUtils';
import logger from './logger';

const SESSION_KEY = 'nl_checkout_customer_is_password_set';

const parsePasswordSetFlag = (rawFlag) =>
	rawFlag === true || rawFlag === 1 || rawFlag === '1';

/**
 * Persist `is_password_set` from customer list when a customer is selected.
 * Survives booking create clearing global `customerData`.
 */
export const setCheckoutCustomerIsPasswordSet = (customerId, isPasswordSet, justSet = false) => {
	if (customerId == null || customerId === '') return;
	if (isPasswordSet === undefined || isPasswordSet === null) return;
	try {
		sessionStorage.setItem(
			SESSION_KEY,
			JSON.stringify({
				customerId: Number(customerId),
				isPasswordSet: parsePasswordSetFlag(isPasswordSet),
				justSet: Boolean(justSet),
			})
		);
	} catch {
		// ignore quota / private mode errors
	}
};

export const getCheckoutCustomerIsPasswordSet = (customerId) => {
	if (customerId == null || customerId === '') return undefined;
	try {
		const raw = sessionStorage.getItem(SESSION_KEY);
		if (!raw) return undefined;
		const parsed = JSON.parse(raw);
		if (Number(parsed?.customerId) !== Number(customerId)) return undefined;
		return parsed.isPasswordSet;
	} catch {
		return undefined;
	}
};

export const clearCheckoutCustomerIsPasswordSet = () => {
	try {
		sessionStorage.removeItem(SESSION_KEY);
	} catch {
		// ignore
	}
};

/**
 * Load `is_password_set` for a user from `/users/customer/list` (Sales checkout / payment top-up).
 */
export const fetchAndStoreCheckoutCustomerPasswordFlag = async (userId) => {
	if (userId == null || userId === '') return undefined;

	// Always call the API for a fresh is_password_set value.
	// Primary: /users/{id} API — returns correct membership_detail.is_password_set
	try {
		const detailRes = await getCustomerDetails(userId);
		const user = detailRes?.data?.data;
		if (user) {
			const flag =
				user?.membership_detail?.is_password_set ??
				user?.is_password_set;
			if (flag !== undefined && flag !== null) {
				const isSet = parsePasswordSetFlag(flag);
				setCheckoutCustomerIsPasswordSet(userId, isSet);
				return isSet;
			}
		}
	} catch {
		// fall through to customer list
	}

	// Fallback: customer list API
	try {
		const { item } = buildCustomerListRequest(
			'',
			null,
			buildMembershipCustomerListExtraParams(userId)
		);
		const response = await getCustomerList({ item });
		const clients =
			response?.data?.data?.list?.clients ??
			response?.data?.list?.clients ??
			[];
		const match = Array.isArray(clients)
			? clients.find((c) => Number(c?.id) === Number(userId)) ?? clients[0]
			: null;
		if (!match || match.is_password_set === undefined || match.is_password_set === null) {
			return getCheckoutCustomerIsPasswordSet(userId);
		}
		const isSet = parsePasswordSetFlag(match.is_password_set);
		setCheckoutCustomerIsPasswordSet(userId, isSet);
		return isSet;
	} catch (error) {
		logger.error('Failed to load customer password flag:', error);
		return getCheckoutCustomerIsPasswordSet(userId);
	}
};

/** After user sets membership PIN — persist so future top-ups skip the set-password step. */
export const markCheckoutCustomerPasswordAsSet = (customerId) => {
	if (customerId == null || customerId === '') return;
	setCheckoutCustomerIsPasswordSet(customerId, true);
};

/** Apply `is_password_set: true` on customer + membership_detail objects. */
export const withMembershipPasswordSetFlag = (record) => {
	if (!record || typeof record !== 'object') return record;
	const next = { ...record, is_password_set: true };
	if (record.membership_detail && typeof record.membership_detail === 'object') {
		next.membership_detail = { ...record.membership_detail, is_password_set: true };
	}
	return next;
};

/**
 * Whether membership password is set — prefers checkout session (customer list at select time),
 * then membership detail / customer record fields.
 */
export const resolveMembershipPasswordIsSet = (
	customerRecord,
	membershipDetailRecord = null
) => {
	const customerId = customerRecord?.id ?? customerRecord?.user_id;
	const fromSession =
		customerId != null ? getCheckoutCustomerIsPasswordSet(customerId) : undefined;
	if (fromSession !== undefined) {
		return fromSession;
	}

	const passwordFlag =
		membershipDetailRecord?.is_password_set ??
		customerRecord?.is_password_set ??
		customerRecord?.isPasswordSet ??
		customerRecord?.password_set;
	return parsePasswordSetFlag(passwordFlag);
};

export const shouldPromptSetMembershipPasswordAfterPurchase = (
	customerRecord,
	membershipDetailRecord = null
) => !resolveMembershipPasswordIsSet(customerRecord, membershipDetailRecord);

/** Merge session `is_password_set` onto a customer object for membership top-up modals. */
export const enrichCustomerWithCheckoutPasswordFlag = (customer) => {
	if (!customer || customer.id == null) return customer;
	const fromSession = getCheckoutCustomerIsPasswordSet(customer.id);
	if (fromSession === undefined) return customer;
	return { ...customer, is_password_set: fromSession };
};

/** Read and clear the `justSet` flag — returns true once if password was just set in this journey. */
export const getAndClearCheckoutCustomerJustSet = (customerId) => {
	if (customerId == null || customerId === '') return false;
	try {
		const raw = sessionStorage.getItem(SESSION_KEY);
		if (!raw) return false;
		const parsed = JSON.parse(raw);
		if (Number(parsed?.customerId) !== Number(customerId)) return false;
		if (!parsed.justSet) return false;
		sessionStorage.setItem(
			SESSION_KEY,
			JSON.stringify({ ...parsed, justSet: false })
		);
		return true;
	} catch {
		return false;
	}
};

/** Persist password flag from a customer list row when present. */
export const storeCheckoutPasswordFlagFromCustomer = (customer) => {
	if (!customer?.id) return;
	if (customer.is_password_set === undefined || customer.is_password_set === null) {
		return;
	}
	// Only store true — backend customer list returns stale is_password_set: 0
	if (parsePasswordSetFlag(customer.is_password_set)) {
		setCheckoutCustomerIsPasswordSet(customer.id, true);
	}
};
